/**
 * Друкована форма «Замовлення постачальнику» (2026-07-29, ревізія Покупок).
 *
 * Довільна форма для відправки постачальнику: Замовник (наша організація) /
 * Постачальник, договір, таблиця позицій з цінами, підсумки, підписи.
 * ЄДИНИЙ стиль друку проекту (body.print-mode + .print-page, кнопки
 * «Друк / PDF» / «Назад»).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supplierOrders } from '@/api/orders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import PrintLines1c, { type PrintLine } from '@/shared/components/PrintLines1c';
import { orderRequisites, type InvoiceDoc } from '@/shared/utils/invoicePrintModel';
import { printLineAmount, vatMode } from '@/shared/utils/vatFlags';
import { partyCodesText } from '@/shared/utils/partyCodes';

export default function SupplierOrderPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['supplier-order', id],
    queryFn: () => supplierOrders.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;

  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const supplier = contractorsQ.data?.items.find((c) => c.id === d?.contractor_id);
  const contractsQ = useQuery({
    queryKey: ['contracts', d?.organization_id, d?.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: d!.organization_id, contractor_id: d!.contractor_id,
    }),
    enabled: !!d,
  });
  const contract = contractsQ.data?.find((c) => c.id === d?.contract_id);
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження замовлення…</div>;
  }

  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const items = d.items.filter((it: { nomenclature_id?: string | null }) => it.nomenclature_id);
  const totalNoVat = items.reduce(
    (s: number, it: { amount_without_vat: string }) => s + Number(it.amount_without_vat), 0);
  const totalVat = items.reduce(
    (s: number, it: { vat_amount: string }) => s + Number(it.vat_amount), 0);
  const total = totalNoVat + totalVat;
  // ч.1374: таблиця, підсумки і прописом — за ознаками ПДВ документа (як у 1С); у шапці —
  // всі заповнені реквізити замовлення/рахунку (умови оплати, термін і умови поставки…).
  const mode = vatMode(d, org.is_vat_payer);
  const vat = mode === 'none' ? 0 : totalVat;
  const lines: PrintLine[] = items.map((it: {
    nomenclature_id: string; quantity: string; price: string;
    amount_without_vat: string; vat_amount: string;
  }) => {
    const nom = nomById.get(it.nomenclature_id);
    return {
      name: nom?.name ?? '—',
      qty: Number(it.quantity),
      uom: nom?.base_uom_id ? (uomById.get(nom.base_uom_id) ?? 'шт') : 'шт',
      price: Number(it.price),
      amount: printLineAmount(it, mode),
    };
  });
  const requisites = orderRequisites(d as unknown as InvoiceDoc, mode === 'none' ? totalNoVat : total);

  const contractLine = contract && (contract as { print_deal_info?: boolean }).print_deal_info !== false
    ? ((contract as { print_name?: string | null }).print_name
      || `${contract.name}${(contract as { number?: string | null }).number ? ` №${(contract as { number?: string | null }).number}` : ''}`)
    : null;

  return (
    <div className="print-page invoice-print" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.supplier_orders',
        documentId: d.id,
        filename: `Замовлення ${d.document_number}`,
        orgName: org.name,
      }} />

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        {d.order_kind === 'PROFORMA_INVOICE' ? 'Рахунок постачальника' : 'Замовлення постачальнику'} № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10.5pt', marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ width: 120, verticalAlign: 'top', color: '#333' }}>Замовник:</td>
            <td>
              <strong>{org.full_name || org.name}</strong><br />
              {partyCodesText(org) && <>{partyCodesText(org)}{', '}</>}
              {org.legal_address ?? ''}
              {org.phone && <>{', тел. '}{org.phone}</>}
            </td>
          </tr>
          <tr>
            <td style={{ verticalAlign: 'top', color: '#333', paddingTop: 6 }}>Постачальник:</td>
            <td style={{ paddingTop: 6 }}>
              <strong>{supplier?.name ?? '—'}</strong>
              {partyCodesText(supplier) && <>{', '}{partyCodesText(supplier)}</>}
              {supplier?.legal_address && <><br />{supplier.legal_address}</>}
            </td>
          </tr>
          {contractLine && (
            <tr>
              <td style={{ color: '#333', paddingTop: 6 }}>Договір:</td>
              <td style={{ paddingTop: 6 }}>{contractLine}</td>
            </tr>
          )}
          {requisites.map(([label, value]) => (
            <tr key={label}>
              <td style={{ color: '#333', paddingTop: 6 }}>{label}:</td>
              <td style={{ paddingTop: 6 }}>{value}</td>
            </tr>
          ))}
          {d.comment && (
            <tr>
              <td style={{ color: '#333', paddingTop: 6 }}>Примітка:</td>
              <td style={{ paddingTop: 6 }}>{d.comment}</td>
            </tr>
          )}
        </tbody>
      </table>

      {d.order_kind !== 'PROFORMA_INVOICE' && (
        <p style={{ margin: '0.5rem 0' }}>
          Просимо поставити такі товари/послуги:
        </p>
      )}

      <PrintLines1c mode={mode} lines={lines} net={totalNoVat} vat={vat} />

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Від Замовника</strong><br /><br />
              _______________________
              {org.director_name && (
                <><br /><span style={{ color: '#333' }}>({org.director_name})</span></>
              )}
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, М.П.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Погоджено (Постачальник)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, М.П.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
